import { motion } from 'framer-motion';
import SectionTitle from '../../components/ui/SectionTitle';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import {
  useLocalizedName,
  useLocalizedDescription,
  useNumberFormatter,
} from '../../lib/utils/formatters';
import type { WineCategory, WineItemWithCategory } from '../../lib/supabase/types';

interface WineListContentProps {
  categoriesWithSlugs: (WineCategory & { slug: string })[];
  groupedWineItems: Record<string, WineItemWithCategory[]>;
  groupedBeverageSubCategories: Record<string, WineItemWithCategory[]>;
}

const WineListContent = ({
  categoriesWithSlugs,
  groupedWineItems,
  groupedBeverageSubCategories,
}: WineListContentProps) => {
  const { t } = useLanguage();
  const getLocalizedName = useLocalizedName();
  const getLocalizedDescription = useLocalizedDescription();
  const { formatPrice } = useNumberFormatter();

  return (
    <div className="space-y-20">
      {categoriesWithSlugs.map(category => (
        <div key={category.id} id={category.slug} className="scroll-mt-[140px]">
          <SectionTitle title={getLocalizedName(category)} align="center" className="mb-10" />

          {category.name === 'Beverages' ? (
            // Render sub-categories for Beverages
            <div className="space-y-8 md:grid md:grid-cols-2 md:gap-8">
              {Object.keys(groupedBeverageSubCategories)
                .sort((a, b) => {
                  // Define a custom order for sub-categories
                  const order = [
                    'Soda & juices',
                    'Coffee',
                    'Draft beer',
                    'Bottled beer',
                    'Cocktails',
                    'Liquors',
                    'Snaps',
                    'Alcoholic Coffee drinks',
                  ];
                  return order.indexOf(a) - order.indexOf(b);
                })
                .map(subCategoryName => (
                  <div
                    key={subCategoryName}
                    className="md:bg-white md:rounded-lg md:shadow-md md:p-6 mb-8 md:mb-0"
                  >
                    <h3 className="font-serif text-xl font-semibold text-neutral-900 mb-6 text-left md:text-center">
                      {t.wineList.subCategories[subCategoryName] || subCategoryName}
                    </h3>
                    <div className="space-y-0">
                      {groupedBeverageSubCategories[subCategoryName].map(wine => (
                        <div
                          key={wine.id}
                          className="flex justify-between items-start py-3 border-b border-neutral-800 md:border-neutral-100 last:border-b-0"
                        >
                          <div className="flex-1 pr-4">
                            <h4 className="font-serif text-base font-semibold text-neutral-900 mb-1">
                              {getLocalizedName(wine)}
                            </h4>
                            <div className="flex items-center gap-2 mb-2">
                              {wine.region && (
                                <span className="text-neutral-600 text-sm">{wine.region}</span>
                              )}
                              {wine.year && (
                                <span className="text-neutral-500 text-sm">{wine.year}</span>
                              )}
                            </div>
                            {wine.description && (
                              <p className="text-neutral-700 text-sm italic">
                                {getLocalizedDescription(wine)}
                              </p>
                            )}
                          </div>
                          <div className="flex-shrink-0 text-right">
                            {wine.price && (
                              <span className="font-medium text-red-800 text-sm block">
                                {formatPrice(wine.price)} ISK
                              </span>
                            )}
                            {wine.small_bottle_price && (
                              <div className="text-xs text-neutral-600">
                                250ml: {formatPrice(wine.small_bottle_price)} ISK
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            // Existing rendering for other categories
            <div className="space-y-0 md:grid md:grid-cols-2 md:gap-8 overflow-y-hidden">
              {(groupedWineItems[category.slug] || []).map((wine, index) => (
                <motion.div
                  key={wine.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="md:bg-white md:rounded-lg md:overflow-hidden md:shadow-md md:hover:shadow-lg transition-shadow relative border-b border-neutral-800 md:border-none last:border-b-0"
                >
                  <div className="px-0 py-4 md:p-4 lg:p-6 w-full">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-serif text-lg sm:text-xl font-semibold text-neutral-900 mb-1">
                          {getLocalizedName(wine)}
                        </h3>
                        <div className="flex justify-between items-center mb-3">
                          {wine.region && (
                            <span className="text-neutral-600 text-xs sm:text-sm">
                              {wine.region}
                            </span>
                          )}
                          {wine.year && (
                            <span className="text-neutral-500 text-xs sm:text-sm">{wine.year}</span>
                          )}
                        </div>
                        {wine.description && (
                          <p className="text-neutral-700 text-xs sm:text-sm italic">
                            {getLocalizedDescription(wine)}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        {wine.price && (
                          <span className="font-medium text-red-800 text-sm sm:text-base block">
                            {formatPrice(wine.price)} ISK
                          </span>
                        )}
                        {wine.small_bottle_price && (
                          <div className="text-xs sm:text-sm text-neutral-600">
                            250ml: {formatPrice(wine.small_bottle_price)} ISK
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default WineListContent;
